<template>
  <!-- prettier-ignore -->
  <ovp-form
    ref="form"
    class="form"
    name="persons"
    tracking-disabled
    :validator="$v.form"
    @cancel="$emit('cancel', form)"
    @submit="submit">
    <ovp-partner
      v-if="hasPartnerWithAdvisorEmail"
      v-model="form"
      :theme="theme"
      :v="$v.form"
    />

    <ovp-persons-collective
      v-if="collective"
      v-model="form"
      :theme="theme"
      :v="$v.form"
    />

    <template v-if="hasPartnerWithAdvisoryProtocol">
      <ovp-typography
        class="form__section-title__advisor"
        tag="div"
        type="heading04"
        v-text="$t('screen.welcome.sectionAdvisor')"
      />

      <ovp-partner-advisory
      :partner="getPartner()"
      />

      <ovp-typography
        class="form__section-title__insured"
        tag="div"
        type="heading04"
        v-text="$t('screen.welcome.sectionInsured')"
      />
    </template>
    <ovp-persons-contract
      v-model="form"
      :theme="theme"
      :v="$v.form"
    />

    <template v-for="(person, index) in form.persons">
      <ovp-persons-person
        :key="index"
        v-model="form.persons[index]"
        :index="index"
        :theme="theme"
        :person-groups="personGroups"
        :v="$v.form.persons.$each[index]"
        @delete="removePerson(index)"
      />
    </template>

    <div :class="{ 'form-actions': true, 'form-actions--single': !allowMultiplePersons }">
      <ovp-button
        v-if="allowMultiplePersons"
        plain
        :disabled="form.persons.length === 7"
        @click="addPerson">
        {{ $t('person.addPerson') }}
      </ovp-button>

      <ovp-button
        primary
        type="submit">
        {{ $t('form.apply') }}
      </ovp-button>
    </div>
  </ovp-form>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { cloneDeep } from 'lodash'

import OvpPartner from '@/components/Person/Partner'
import OvpPersonsCollective from '@/components/Person/Collective'
import OvpPersonsContract from '@/components/Person/Contract'
import OvpPersonsPerson from '@/components/Person/Person'

import { EVENT_OVP_ENTERED, EVENT_PERSONS_FORM_DISPLAYED } from '@/config/events'

import { BasketHelper, DateTimeHelper, DistributionHelper, LogHelper, PersonHelper, CollectiveHelper } from '@/helpers'

import BasketMixin from '@/mixins/Basket'

import validations from '@/validations/entry'

import { DEFAULT_MASTER_TREATY, DISTRIBUTION_PARTNER_WITH_ADVISOR_MAIL } from '@/config/constants'
import OvpTypography from '@/components/Container/Typography'
import OvpPartnerAdvisory from '@/components/Person/PartnerAdvisory'

export default {
  name: 'Contract',

  mixins: [BasketMixin],

  components: {
    OvpPartnerAdvisory,
    OvpTypography,
    OvpPartner,
    OvpPersonsCollective,
    OvpPersonsContract,
    OvpPersonsPerson,
  },

  props: {
    source: {
      type: String,
      required: true,
    },

    theme: {
      type: String,
      default: 'light',
    },
  },

  data() {
    return {
      form: {
        immigrant: false,
        contractNumber: null,
        contractStartDate: null,
        dateOfEntry: null,
        address: {},
        persons: [],
        salesPartnerEmail: null,
      },
    }
  },

  validations,

  mounted() {
    this.$EventBus.$emit(EVENT_PERSONS_FORM_DISPLAYED, {
      source: this.source,
    })
  },

  watch: {
    address: {
      immediate: true,
      handler: function (value) {
        this.form.address = value || {}
        this.$v.$reset()
      },
    },

    collective: {
      immediate: true,
      handler: function (value) {
        this.form.contractNumber = value?.contractNumber || null
        this.$v.$reset()
      },
    },

    contractStartDate: {
      immediate: true,
      handler: function (value) {
        this.form.contractStartDate = value
        this.$v.$reset()
      },
    },

    immigrant: {
      immediate: true,
      handler: function (value) {
        this.form.immigrant = !!value
        this.$v.$reset()
      },
    },

    persons: {
      immediate: true,
      handler: function (value) {
        this.form.persons = Array.isArray(value) ? cloneDeep(value) : []

        if (this.form.persons.length === 0) {
          this.addPerson()
        } else if (this.immigrant) {
          this.form.dateOfEntry = this.form.contractStartDate
        } else {
          this.form.immigrant = value.some(v => v.preInsurer?.currentKvgMovedToCH === true)
          this.form.dateOfEntry = value.find(v => v.preInsurer?.currentRegistration)?.preInsurer?.currentRegistration
        }
        this.$v.$reset()
      },
    },

    salesPartnerEmail: {
      immediate: true,
      handler: function (value) {
        this.form.salesPartnerEmail = value
        this.$v.$reset()
      },
    },
  },

  computed: {
    ...mapState(['basket']),
    ...mapState('basket', ['address', 'collective', 'contractStartDate', 'immigrant', 'persons', 'salesPartnerEmail']),
    ...mapGetters('content', ['getCollective', 'getPartner', 'isPartnerBroker']),

    allowMultiplePersons() {
      return true
      // return this.basket.masterTreaty === 'SA-EV'
    },

    personGroups() {
      return this.form.contractNumber ? this.collectiveContract?.personGroups : []
    },

    collectiveContract() {
      return this.getCollective()
    },

    hasPartnerWithAdvisorEmail() {
      const { masterTreaty } = this.basket
      if (!this.basket.distributionPartners || !masterTreaty) return false
      return DistributionHelper.hasPartner(this.basket) && DISTRIBUTION_PARTNER_WITH_ADVISOR_MAIL.includes(masterTreaty)
    },
    hasPartnerWithAdvisoryProtocol() {
      return this.isPartnerBroker
    },
  },

  methods: {
    ...mapActions('content', ['setCollective']),

    addPerson() {
      this.form.persons.push(PersonHelper.createPerson())
    },

    /**
     * In case of Collective: Check, if collective has been exited
     * In normal-cases: resolve with basket
     */
    hasLeftCollective(basket) {
      if (this.collective?.contractNumber && !this.form.contractNumber) {
        return this.$confirm(this.$t('person.confirmCollectiveExit')).then(() => {
          this.setCollective(null)
          basket.collective = null
          basket.distributionPartners = null
          basket.masterTreaty = DEFAULT_MASTER_TREATY
          basket.persons = basket.persons.map(person => {
            person.collectivePersonGroup = null
            return person
          })

          return basket
        })
      } else {
        return Promise.resolve(basket)
      }
    },

    processImmigration(basket) {
      const contractStartDate = basket.contractStartDate

      if (basket.immigrant) {
        basket.persons = basket.persons.map(person => {
          person.preInsurer = person.preInsurer || {}
          person.preInsurer.currentRegistration = contractStartDate
          person.preInsurer.currentKvgMovedToCH = true
          person.preInsurer.preInsurer = false

          return person
        })
      } else {
        basket.persons = basket.persons.map(person => {
          person.preInsurer = person.preInsurer || {}
          person.preInsurer.currentRegistration = null
          person.preInsurer.currentKvgMovedToCH = false
          person.preInsurer.preInsurer = true

          return person
        })
      }

      return Promise.resolve(basket)
    },

    removePerson(index) {
      this.form.persons.splice(index, 1)

      if (this.form.persons.length === 0) {
        this.addPerson()
      }

      this.$v.form.$touch()
    },

    addOrderToPersons(persons) {
      return persons.map((person, index) => {
        return {
          ...person,
          order: index + 1,
        }
      })
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    submit() {
      let basket = this.form

      if (basket.immigrant) {
        // if unborn, remove immigrant-option
        const fpAge = DateTimeHelper.getAge(basket.persons[0].personData.dateOfBirth)
        if (basket.persons.length === 1 && fpAge < 0) {
          basket.immigrant = false
          basket.dateOfEntry = null
        } else {
          basket.contractStartDate = basket.dateOfEntry
        }
      }

      basket.persons = this.addOrderToPersons(basket.persons)

      const hadPersons = BasketHelper.hasPersons(this.basket)
      return this.hasLeftCollective(basket)
        .then(basket => this.processImmigration(basket))
        .then(basket => BasketHelper.updateBasket(basket, true))
        .then(basket => {
          return {
            basket,
            askConfirmation: BasketHelper.checkNeedsConfirmation(basket, this.basket),
          }
        })
        .then(({ basket, askConfirmation }) => {
          if (askConfirmation) {
            return BasketHelper.askChangeConfirmation(this.$t('person.confirmChange')).then(() => basket)
          } else {
            return basket
          }
        })
        .then(basket => {
          this.$loading.start()

          if (!basket.createDate) {
            if (!basket.contractStartDate) delete basket.contractStartDate
            delete basket.contractNumber
            delete basket.masterTreaty
            delete basket.personId

            return this.$backend.ovp
              .createBasket(basket)
              .then(basket => BasketHelper.updateBasketSession(basket))
              .then(basket => BasketHelper.setLanguage(basket))
              .then(basket => CollectiveHelper.loadCollective(basket))
              .then(basket => BasketHelper.getAndSetProducts(basket))
              .then(basket => BasketHelper.getAndSetDistributionPartnerByBasket(basket))
          } else {
            return this.updateBasket(basket)
          }
        })
        .then(basket => {
          // If no persons were in the basket before => track EVENT_OVP_ENTERED
          // Otherwise OvpEntered tracked twice when coming from pricewidget
          if (!hadPersons) {
            this.$EventBus.$emit(EVENT_OVP_ENTERED)
          }
          this.$emit('submit', basket)
          return basket
        })
        .catch(e => {
          if (e) {
            LogHelper.error(['Contract submit failed', e])
          }
        })
        .finally(() => this.$loading.end())
    },
  },
}
</script>

<style name="mobile" scoped>
.form {
  width: 100%;

  .form__section-title__advisor {
    margin: 20px 0 20px;
  }

  .form__section-title__insured {
    margin: 0;
  }

  &-actions {
    display: flex;
    flex-direction: column;
    margin: 20px 0 0 0;

    .plain {
      color: var(--c-original-green);
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .form {
    .form__section-title__advisor {
      margin: 30px 0 20px;
    }

    &-actions {
      flex-direction: row;
      justify-content: space-between;

      &--single {
        justify-content: flex-end;
      }
    }
  }
}
</style>
